import { defineStore } from 'pinia';
import { type RemovableRef, useStorage } from '@vueuse/core';
import type { StudioDisplayModel } from '@shared/models/studios/studios';
import type { SubjectDisplayModel } from '@shared/models/subjects/subjects';
import type { identifier } from '@core/models/base';
import { isNullOrUndefined } from '@shared/utils';
import { type ServiceError, isServiceError, isServiceSuccess } from '@core/models/actionResponse';
import type { StudioJobPreferenceDisplayModel } from '@shared/models/jobs/jobPreferences';
import type { GreenScreenBackgroundDisplayModel } from '@shared/models/packages/greenScreenBackgrounds';
import type { BackgroundDisplayModel } from '@shared/models/packages/backgroundSets';
import { fetchGreenScreens } from '@apps/customer-package-builder/services/greenscreen.service';
import { fetchJobFonts, useFontLoader } from '@shared/services/fonts';
import type {
  JobDisplayModelV3,
  JobDisplayModelV3GallerySettings,
  JobDisplayModelV3PrepaySettings,
} from '@shared/models/jobs/v3/displayModels';
import { getStudioDetails } from '../services/studio.service';
import { fetchJobDetails } from '../services/jobs.service';
import { getJobPreferences } from '../services/preferences.service';
import { useCartStore } from './cart.store';

export interface CustomerState {
  job?: JobDisplayModelV3;
  /**
   * Tri-state on purpose. `undefined` means "we do not know yet" — never loaded, or the load
   * failed; `[]` means "loaded, this job has none". Collapsing the two is what let a session that
   * failed to load the backgrounds look identical to a job with no backgrounds, so the consumer
   * was never shown the picker and never blocked from checking out without one. Read it through
   * `greenScreensKnown` / `hasGreenScreens` rather than testing `.length` directly.
   */
  greenScreens?: GreenScreenBackgroundDisplayModel[];
  studio?: StudioDisplayModel;
  subject?: SubjectDisplayModel;
  studioSlug: string | undefined;
  loading: boolean;
  accessCode: RemovableRef<string | null>;
  cartStatus?: boolean;
  persistedJobId: RemovableRef<identifier | null>;
  persistedStudioId: RemovableRef<identifier | null>;
  jobPreferences?: StudioJobPreferenceDisplayModel;
  subjectImageCount: number;
  /**
   * The job whose details AND preferences finished loading. `job?.id` is not a substitute: it is
   * assigned mid-load, so it reports "loaded" while later fetches are still outstanding.
   */
  loadedJobId?: number;
}

/**
 * In-flight load trackers, deliberately module-scoped rather than store state: they hold Promises
 * (which have no business in a reactive proxy) and exist only to make concurrent callers join an
 * existing request instead of racing or no-opping. Cleared once settled, so a failed load is
 * retried by the next caller rather than being cached as a failure.
 */
let studioLoad: { slug: string; promise: Promise<ServiceError | null> } | undefined;
let jobLoad: { jobId: number; promise: Promise<void> } | undefined;
let greenScreenLoad: { jobId: number; promise: Promise<boolean> } | undefined;

/**
 * Warm the web fonts the job's package sets reference so the first live preview draws with the
 * right glyphs. Fire-and-forget and outside store state on purpose: it only touches the shared
 * font registry, and MLivePreview's own font gate covers the case where this never lands.
 */
async function preloadJobFonts(jobId: number): Promise<void> {
  try {
    const fonts = await fetchJobFonts(jobId);
    if (fonts.length) useFontLoader().preloadFonts(fonts);
  } catch (e) {
    console.warn('[CustomerStore] Font preload failed', e);
  }
}
export const useCustomerStore = defineStore('customerStore', {
  state: (): CustomerState => ({
    job: undefined,
    studio: undefined,
    subject: undefined,
    studioSlug: undefined,
    jobPreferences: undefined,
    loading: false,
    cartStatus: undefined,
    greenScreens: undefined,
    accessCode: useStorage<string>('customer-accesssCode', null),
    persistedJobId: useStorage<identifier>('customer-jobId', null),
    persistedStudioId: useStorage<identifier>('customer-studioId', null),
    subjectImageCount: 0,
    loadedJobId: undefined,
  }),
  getters: {
    jobId: (state): number => state.job?.id || 0,
    studioId: (state): number => state.studio?.id || state.persistedStudioId || 0,
    /**
     * RS+ Hosted Job
     */
    isHosted(state): boolean {
      const cartStore = useCartStore();
      return cartStore.persistedOrderMethod === 'Gallery';
    },
    /**
     * RS+ Prepay Job
     */
    isPrePay(state): boolean {
      const cartStore = useCartStore();
      return cartStore.persistedOrderMethod === 'Prepay';
    },
    /**
     * Returns true if the job is prepay and is a V3 (RSPO-behavior) job.
     * Gates on `isV3` (Version >= 3), NOT `rspoJob` — `rspoJob` only records that the job
     * was created in the marketplace (vs imported from RS+), so legacy-style jobs created
     * in the marketplace have it set and must not get the RSPO consumer experience.
     */
    isPrePayRspo(state): boolean {
      return !!(state.job?.isV3 && this.isPrePay);
    },
    /**
     * Returns true if the job is hosted and is a V3 (RSPO-behavior) job.
     * See `isPrePayRspo` for why this gates on `isV3` rather than `rspoJob`.
     */
    isHostedRspo(state): boolean {
      return !!(state.job?.isV3 && this.isHosted);
    },
    /**
     * True once we actually know the job's green screen backgrounds — either some or none.
     * Callers that gate a required selection MUST check this: "unknown" has to behave like
     * "might be required", never like "none configured".
     */
    greenScreensKnown: (state): boolean => state.greenScreens !== undefined,

    /**
     * Returns true if the job has green screens. False while still unknown — pair it with
     * `greenScreensKnown` anywhere the answer decides whether a step can be skipped.
     */
    hasGreenScreens: (state): boolean => !!state.greenScreens?.length,

    /**
     * Returns sorted backgrounds from the job's background set
     */
    backgrounds: (state): BackgroundDisplayModel[] => {
      const cartStore = useCartStore();
      const orderMethod = cartStore.persistedOrderMethod;
      const backgroundSet =
        orderMethod === 'Gallery'
          ? state.job?.galleryBackgroundSet
          : orderMethod === 'Prepay'
            ? state.job?.prepayBackgroundSet
            : undefined;

      if (!backgroundSet?.backgrounds) return [];
      return [...backgroundSet.backgrounds].sort((a, b) => a.sortIndex - b.sortIndex);
    },

    /**
     * Returns true if the job has backgrounds
     */
    hasBackgrounds: (state): boolean => {
      const cartStore = useCartStore();
      const orderMethod = cartStore.persistedOrderMethod;
      const backgroundSet =
        orderMethod === 'Gallery'
          ? state.job?.galleryBackgroundSet
          : orderMethod === 'Prepay'
            ? state.job?.prepayBackgroundSet
            : undefined;
      return !!backgroundSet?.backgrounds?.length;
    },

    prepaySettings(state): JobDisplayModelV3PrepaySettings | undefined {
      return state.job?.prepaySettings;
    },

    gallerySettings(state): JobDisplayModelV3GallerySettings | undefined {
      return state.job?.gallerySettings;
    },

    /**
     * Returns true if the job is prepay and imported data
     */
    hasImportedData(state): boolean {
      const version = state.job?.version;

      if (version && version.major >= 3) {
        return state.job?.importedData ?? false;
      } else {
        return this.prepaySettings?.importedData ?? false;
      }
    },

    /**
     * Hosted studio has enabled yearbook pose selection and selection time has not expired
     */
    canSelectYearBookPose(state): boolean {
      return !!(
        this.gallerySettings?.enableYearBookPoseSelection &&
        this.gallerySettings?.isYearBookPoseSelectionExpired !== true &&
        state.subjectImageCount > 1
      );
    },
  },
  actions: {
    persistState(jobId: identifier, accessCode: string | null, studioId: identifier | null) {
      this.accessCode = accessCode;
      this.persistedJobId = jobId;
      this.persistedStudioId = studioId;
    },
    clearPersistedState() {
      this.accessCode = null;
      this.persistedJobId = null;
      this.persistedStudioId = null;
    },
    setSubjectImageCount(count: number) {
      this.subjectImageCount = count;
    },
    /**
     * Load the studio and (when a job is addressed) that job's details, preferences and green
     * screen backgrounds. Safe to call concurrently and safe to call again after a failure —
     * `ensureStudio` / `ensureJob` join an in-flight request rather than racing it.
     *
     * The previous version stamped `this.studioSlug` synchronously before awaiting the studio
     * fetch, then gated the job branch on `this.studio`. A second call arriving during that await
     * therefore skipped the studio fetch (slug already matched) AND skipped the job branch
     * (`this.studio` still undefined), so it returned having done nothing — while the first call
     * resumed and evaluated its own, older `jobId`. The result was a session with no job and no
     * green screens that never retried, because the only callers are two route-param watchers.
     */
    async initialize(studioSlug: string, jobId?: identifier | null): Promise<ServiceError | null> {
      this.loading = true;
      try {
        const studioError = await this.ensureStudio(studioSlug);
        if (studioError) return studioError;

        const targetJobId = isNullOrUndefined(jobId) || isNaN(jobId as number) ? null : Number(jobId);

        if (targetJobId !== null) {
          await this.ensureJob(targetJobId);
        } else if (this.loadedJobId || this.job) {
          this.clearJob();
        }

        return null;
      } finally {
        setTimeout(() => {
          this.loading = false;
        }, 1000);
      }
    },

    /**
     * Resolve the studio for `studioSlug`, joining an in-flight load for the same slug. State is
     * assigned only after the fetch succeeds, so `studioSlug`/`studio` never advertise a studio
     * that has not actually loaded. Assumes the slug does not change mid-session (it is the path
     * segment the consumer arrived on).
     */
    async ensureStudio(studioSlug: string): Promise<ServiceError | null> {
      if (this.studio && this.studioSlug === studioSlug) return null;
      if (studioLoad?.slug === studioSlug) return await studioLoad.promise;

      const promise = (async (): Promise<ServiceError | null> => {
        const details = await getStudioDetails(studioSlug);
        if (isServiceError(details)) return details;
        if (!details.data.rspoEnabled) return { errors: ['There was an error loading the studio'] };
        this.studioSlug = studioSlug;
        this.studio = details.data;
        return null;
      })();

      studioLoad = { slug: studioSlug, promise };
      try {
        return await promise;
      } finally {
        studioLoad = undefined;
      }
    },

    /**
     * Load a job's details, preferences and green screen backgrounds, joining an in-flight load
     * for the same job. `loadedJobId` is set only after details and preferences both land, so a
     * partial load is never mistaken for a complete one and the next caller retries.
     */
    async ensureJob(jobId: number): Promise<void> {
      if (this.loadedJobId === jobId) {
        // Details are in place; the backgrounds may still be unknown from a failed attempt.
        await this.ensureGreenScreens();
        return;
      }
      if (jobLoad?.jobId === jobId) return await jobLoad.promise;

      const promise = (async (): Promise<void> => {
        const job = await fetchJobDetails(jobId);
        void preloadJobFonts(job.id);
        const preferences = await getJobPreferences(this.studio!.id, job.id);

        this.job = job;
        this.jobPreferences = preferences.data;
        this.greenScreens = undefined;
        this.loadedJobId = jobId;

        await this.ensureGreenScreens();
      })();

      jobLoad = { jobId, promise };
      try {
        await promise;
      } finally {
        jobLoad = undefined;
      }
    },

    /**
     * Load the green screen backgrounds for the currently loaded job. Returns whether they are
     * known afterwards. On failure `greenScreens` is left `undefined` (not `[]`) so consumers can
     * tell "unknown" from "none" and call this again to recover — a failed fetch here used to be
     * swallowed into an empty array that no code ever retried.
     */
    async ensureGreenScreens(): Promise<boolean> {
      const jobId = this.loadedJobId;
      if (isNullOrUndefined(jobId)) return false;
      if (this.greenScreens !== undefined) return true;
      if (greenScreenLoad?.jobId === jobId) return await greenScreenLoad.promise;

      const promise = (async (): Promise<boolean> => {
        const result = await fetchGreenScreens(jobId as number);
        if (!isServiceSuccess(result)) return false;
        // Guard against a job switch completing while this was in flight.
        if (this.loadedJobId !== jobId) return false;
        this.greenScreens = result.data;
        return true;
      })();

      greenScreenLoad = { jobId: jobId as number, promise };
      try {
        return await promise;
      } finally {
        greenScreenLoad = undefined;
      }
    },
    async resetJob() {
      if (this.job) {
        this.job = await fetchJobDetails(this.jobId);
      }
    },
    clearJob() {
      this.job = undefined;
      this.jobPreferences = undefined;
      // undefined, not [] — no job means no answer about backgrounds, not "this job has none".
      this.greenScreens = undefined;
      this.loadedJobId = undefined;
      this.subjectImageCount = 0;
      jobLoad = undefined;
      greenScreenLoad = undefined;
    },
    clear() {
      this.clearJob();
      this.studio = undefined;
      this.subject = undefined;
      this.studioSlug = undefined;
      this.subjectImageCount = 0;
      studioLoad = undefined;
    },
  },
});
